import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';
import { ContactUs } from 'src/app/shared/services/contact-us/contact-us';
import { ContactUsService } from 'src/app/shared/services/contact-us/contact-us.service';
import { ErrorComponent, InformationComponent } from 'src/app/shared/util/helpers';
import { TrackerService } from 'src/app/shared/util/tracker.service';
import { filter, finalize } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Sticky header (~4rem) + small gap */
const SCROLL_HEADER_OFFSET = 80;

@Component({
  selector: 'app-footer-collaboration',
  templateUrl: './footer-collaboration.component.html',
  styleUrls: ['./footer-collaboration.component.scss'],
})
export class FooterCollaborationComponent implements OnInit, AfterViewInit, OnDestroy {
  contactItem: ContactUs = new ContactUs();
  assistantHref = '';
  isLoading = false;
  contactSuccess = false;

  private routerEventsSub: Subscription | null = null;
  private fragmentScrollTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private trackerService: TrackerService,
    private contactUsService: ContactUsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.trackerService.trackPageView('Footer Collaboration');
    this.assistantHref = environment.assistantApp;

    this.contactItem.subject = '';
    this.contactItem.content = '';
    this.contactItem.email = '';
    this.contactItem.telephone = '';

    this.routerEventsSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        const fragment = this.getUrlFragment();
        if (fragment) {
          this.scheduleScrollToFragment(fragment);
        }
      });

    const initialFragment = this.getUrlFragment();
    if (initialFragment) {
      this.scheduleScrollToFragment(initialFragment);
    }
  }

  ngAfterViewInit(): void {
    const fragment = this.getUrlFragment();
    if (fragment) {
      this.scheduleScrollToFragment(fragment);
    }
  }

  ngOnDestroy(): void {
    this.routerEventsSub?.unsubscribe();
    if (this.fragmentScrollTimer) {
      clearTimeout(this.fragmentScrollTimer);
    }
  }

  send(): void {
    if (!this.contactItem.subject || !this.contactItem.email || !this.contactItem.content) {
      this.snackBar.openFromComponent(ErrorComponent, {
        data: { message: 'Vă rugăm să completați toate câmpurile.' },
        duration: 2000,
      });
      return;
    }

    this.isLoading = true;
    this.contactSuccess = false;
    this.contactItem.date = new Date();

    this.contactUsService
      .addItem(this.contactItem)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        () => {
          this.contactItem.subject = '';
          this.contactItem.email = '';
          this.contactItem.content = '';
          this.contactSuccess = true;
          this.snackBar.openFromComponent(InformationComponent, {
            data: { message: 'Mesajul a fost trimis cu succes!' },
            duration: 2000,
          });
        },
        () => {
          this.snackBar.openFromComponent(ErrorComponent, {
            data: { message: 'A apărut o eroare. Vă rugăm să încercați din nou.' },
            duration: 2000,
          });
        }
      );
  }

  private getUrlFragment(): string | null {
    return this.router.parseUrl(this.router.url).fragment;
  }

  private scheduleScrollToFragment(fragment: string): void {
    if (this.fragmentScrollTimer) {
      clearTimeout(this.fragmentScrollTimer);
    }
    this.fragmentScrollTimer = setTimeout(() => this.scrollToFragment(fragment, 0), 50);
  }

  private scrollToFragment(fragment: string, attempt: number): void {
    const target = document.getElementById(fragment);
    if (target) {
      const top =
        target.getBoundingClientRect().top + window.pageYOffset - SCROLL_HEADER_OFFSET;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: attempt === 0 ? 'smooth' : 'auto',
      });
      return;
    }

    if (attempt < 20) {
      setTimeout(() => this.scrollToFragment(fragment, attempt + 1), 100);
    }
  }
}
